import type { PortalStepKey, PortalViewOutput } from '@garage/shared';
import type { ReactNode } from 'react';
import { StorageTermsSummary } from '../../garage/components/StorageTermsSummary.js';
import { ProgressSteps, stepsOf } from '../../garage/components/ProgressSteps.js';
import { PORTAL_STATUS_LABELS, formatDate, formatDateTime, formatKm, formatWeekdayDate } from '../../garage/lib/format.js';
import { overdueDays } from '../../garage/lib/storage-state.js';
import { buttonClasses } from '../../ui/Button.js';
import { cx } from '../../ui/cx.js';
import { ClipboardIcon, DecisionIcon, MessageIcon } from '../../ui/icons.js';
import { usePortalNav } from '../nav.js';
import { FindUs, hasFindUs } from './FindUs.js';
import { GarageContact } from './GarageContact.js';
import { Block, CARD, HERO, VehicleLine } from './portal-ui.js';
import { ReleaseReportBlock } from './ReleaseReportBlock.js';

/** Précision de l'étape en cours, pour le client (lue par les lecteurs d'écran). */
const PORTAL_STEP_HINTS: Partial<Record<PortalStepKey, string>> = {
  decision: 'votre accord est attendu',
  waiting_parts: 'pièces commandées',
  ready: 'vous pouvez venir le chercher',
};

/** Carte d'action : ce que le client doit faire maintenant, avec un bouton vers l'onglet concerné. */
function ActionCard({ icon, title, text, action, onClick, tone }: { icon: ReactNode; title: string; text: string; action: string; onClick: () => void; tone: 'warning' | 'info' }) {
  return (
    <div className={cx('flex items-start gap-3 rounded-2xl border p-(--card-p)', tone === 'warning' ? 'border-warning-line bg-warning-bg text-warning-fg' : 'border-info-line bg-info-bg text-info-fg')}>
      <span aria-hidden className="mt-0.5 shrink-0">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium">{title}</p>
        <p className="text-base">{text}</p>
        <button type="button" onClick={onClick} className={buttonClasses({ variant: 'secondary', className: 'mt-3 w-full sm:w-auto' })}>
          {action}
        </button>
      </div>
    </div>
  );
}

/** Invitation permanente à écrire au garage (contact cadré, la réponse arrive ici et par e-mail). */
function WriteCard({ garageName, unread, onWrite }: { garageName: string; unread: number; onWrite: () => void }) {
  return (
    <div className={cx(CARD, 'space-y-3')} data-testid="write-card">
      <div className="flex items-start gap-3">
        <span aria-hidden className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-strong">
          <MessageIcon size={20} />
        </span>
        <div className="min-w-0">
          <p className="text-base font-medium text-ink">Une question ? Écrivez au garage</p>
          <p className="text-base text-ink-2">
            {garageName} vous répond ici et par e-mail. Vous pouvez joindre des photos.
          </p>
          {unread > 0 ? (
            <p className="mt-1 text-base font-medium text-ink">
              {unread} nouveau{unread > 1 ? 'x' : ''} message{unread > 1 ? 's' : ''} du garage
            </p>
          ) : null}
        </div>
      </div>
      <button type="button" onClick={onWrite} className={buttonClasses({ variant: 'primary', block: true, className: 'sm:w-auto' })}>
        <MessageIcon size={18} /> {unread > 0 ? 'Lire et répondre' : 'Écrire au garage'}
      </button>
    </div>
  );
}

/**
 * Onglet Suivi : où en est le véhicule (statut, frise d'étapes, dates), ce que le client doit faire, un accès
 * direct aux messages et le bloc « Nous trouver ». Compte rendu : dates et kilométrages de l'intervention.
 */
export function SummaryTab({ view, unread }: { view: PortalViewOutput; unread: number }) {
  const nav = usePortalNav();
  const { garage, intervention: it, vehicle } = view;
  const tz = it.timezone;
  const report = view.mode === 'report';
  const released = it.status === 'closed';
  const toDecide = report ? [] : view.complements.filter((c) => c.decidable);
  const reception = view.reception;
  const write = report ? null : () => nav?.goTo('messages', 'ecrire');

  return (
    <>
      <section className={HERO}>
        <VehicleLine vehicle={vehicle} />
        <h1 className="mt-3 text-2xl font-semibold tracking-[-0.02em] text-ink sm:text-3xl">
          {report ? 'Compte rendu d’intervention' : PORTAL_STATUS_LABELS[it.status]}
        </h1>
        {report ? null : <p className="mt-1 text-sm text-ink-3">Mis à jour le {formatDateTime(it.updatedAt, tz)}</p>}
        {/* Frise d’étapes à icônes (vague 7) : étapes du contrat, le même composant que la fiche dossier du garage. */}
        <ProgressSteps steps={stepsOf(view.steps, it.status, view.complements.length > 0)} hints={PORTAL_STEP_HINTS} label="Étapes" className="mt-6" />
        {!report && it.status === 'ready' ? (
          <div className="mt-5 space-y-3 rounded-xl border border-success-line bg-success-bg p-3.5 text-success-fg">
            <p>
              Vous pouvez venir le chercher.
              {it.storageWaived ? (
                <> Gardiennage offert par le garage.</>
              ) : it.freeUntil && overdueDays(it.freeUntil) > 0 ? (
                <>
                  {' '}
                  Le délai de retrait gratuit a pris fin le <strong>{formatWeekdayDate(it.freeUntil, tz)}</strong> : des frais de gardiennage peuvent
                  s’appliquer, selon les conditions remises à l’entrée.
                </>
              ) : it.freeUntil ? (
                <>
                  {' '}
                  Retrait gratuit jusqu’au <strong>{formatWeekdayDate(it.freeUntil, tz)}</strong>.
                </>
              ) : null}
            </p>
            <GarageContact garage={garage} compact />
          </div>
        ) : null}
        {report ? (
          <p className="mt-5 rounded-xl bg-sunken p-3.5 text-sm text-ink-2">
            Ce compte rendu reste consultable. Conservez ce lien pour le retrouver, par exemple lors d’une revente ou d’un prochain entretien.
          </p>
        ) : released ? (
          <p className="mt-5 text-sm text-ink-3">Ce lien de suivi reste consultable pendant une durée limitée après la restitution du véhicule.</p>
        ) : null}
      </section>

      {toDecide.length > 0 || (!report && reception?.acknowledgeable) ? (
        <div className="space-y-3 px-4 pt-6 sm:px-0">
          {toDecide.length > 0 ? (
            <ActionCard
              tone="warning"
              icon={<DecisionIcon size={20} />}
              title="Votre accord est attendu"
              text={
                toDecide.length > 1
                  ? `${toDecide.length} propositions de travaux complémentaires attendent votre réponse.`
                  : `Le garage vous propose des travaux complémentaires : « ${toDecide[0]?.title ?? ''} ».`
              }
              action="Consulter et décider"
              onClick={() => nav?.goTo('work', 'decision')}
            />
          ) : null}
          {!report && reception?.acknowledgeable ? (
            <ActionCard
              tone="info"
              icon={<ClipboardIcon size={20} />}
              title="Fiche de réception à consulter"
              text="L’état de votre véhicule à son arrivée a été relevé. Prenez-en connaissance."
              action="Voir la fiche"
              onClick={() => nav?.goTo('reception')}
            />
          ) : null}
        </div>
      ) : null}

      {write ? (
        <div className="px-4 pt-6 sm:px-0">
          <WriteCard garageName={garage.displayName} unread={unread} onWrite={write} />
        </div>
      ) : null}

      <Block title={report ? 'Dates et kilométrage' : 'Dates'}>
        <dl className={cx(CARD, 'grid grid-cols-2 gap-x-4 gap-y-3 text-sm')}>
          <div>
            <dt className="text-ink-3">Déposé le</dt>
            <dd className="font-medium text-ink tabular-nums">{report ? formatDate(it.receivedAt, tz) : formatDateTime(it.receivedAt, tz)}</dd>
          </div>
          {it.readyAt && !report ? (
            <div>
              <dt className="text-ink-3">Prêt depuis le</dt>
              <dd className="font-medium text-ink tabular-nums">{formatDateTime(it.readyAt, tz)}</dd>
            </div>
          ) : null}
          {it.releasedAt ? (
            <div>
              <dt className="text-ink-3">Restitué le</dt>
              <dd className="font-medium text-ink tabular-nums">{report ? formatDate(it.releasedAt, tz) : formatDateTime(it.releasedAt, tz)}</dd>
            </div>
          ) : null}
          {report ? (
            <div>
              <dt className="text-ink-3">Kilométrage à l’entrée</dt>
              <dd className="font-medium text-ink tabular-nums">{formatKm(it.entryMileage)}</dd>
            </div>
          ) : null}
          {report && it.exitMileage !== null ? (
            <div>
              <dt className="text-ink-3">Kilométrage à la sortie</dt>
              <dd className="font-medium text-ink tabular-nums">{formatKm(it.exitMileage)}</dd>
            </div>
          ) : null}
        </dl>
      </Block>

      {report ? <ReleaseReportBlock view={view} /> : null}

      {report ? (
        it.storageWaived ? (
          <Block title="Gardiennage">
            <StorageTermsSummary terms={it.storageTerms} waived />
          </Block>
        ) : null
      ) : (
        <Block title="Récupérer votre véhicule">
          <div className={`${CARD} space-y-2`}>
            {it.readyAt ? (
              <p>Disponible depuis le {formatDateTime(it.readyAt, tz)}.</p>
            ) : released ? null : (
              <p>Vous serez prévenu par e-mail dès que le véhicule sera prêt.</p>
            )}
            {it.freeUntil && !it.storageWaived && !it.releasedAt ? (
              overdueDays(it.freeUntil) > 0 ? (
                <p>
                  Le délai de retrait gratuit a pris fin le <strong>{formatWeekdayDate(it.freeUntil, tz)}</strong>. Des frais de gardiennage peuvent
                  s’appliquer, selon les conditions ci-dessous.
                </p>
              ) : (
                <p>
                  Retrait gratuit jusqu’au <strong>{formatWeekdayDate(it.freeUntil, tz)}</strong>.
                </p>
              )
            ) : null}
            {it.releasedAt ? <p>Véhicule restitué le {formatDateTime(it.releasedAt, tz)}.</p> : null}
            <StorageTermsSummary terms={it.storageTerms} waived={it.storageWaived} />
          </div>
        </Block>
      )}

      {/* Vague 7 : bloc « Nous trouver » (itinéraire, appeler, écrire, ouvert jusqu'à…), composant autonome. */}
      {hasFindUs(garage) ? (
        <Block title="Nous trouver">
          <FindUs garage={garage} timezone={tz} onWrite={write} />
        </Block>
      ) : null}
    </>
  );
}
